/**
 * PDF Generator
 * Generates PDF reports using jsPDF
 *
 * NOTE: jsPDF and jspdf-autotable are dynamically imported to reduce initial bundle size.
 * These libraries are only loaded when PDF export is actually used (~411KB savings).
 */

import type { PdfOptions, ColumnConfig, FormatOptions, ExportableEntity } from './types';
import { formatValue, DEFAULT_FORMAT_OPTIONS } from './formatters';
import { format } from 'date-fns';

// Type for jsPDF - we don't import the actual library at the top level
type JsPDFInstance = {
  internal: { pageSize: { getWidth: () => number; getHeight: () => number } };
  setFontSize: (size: number) => void;
  setTextColor: (...color: number[]) => void;
  text: (text: string, x: number, y: number, options?: { align?: string }) => void;
  addPage: () => void;
  output: (type: 'blob') => Blob;
  autoTable: (options: AutoTableOptions) => void;
  lastAutoTable: { finalY: number };
};

interface AutoTableOptions {
  head?: string[][];
  body?: string[][];
  startY?: number;
  theme?: 'striped' | 'grid' | 'plain';
  headStyles?: Record<string, unknown>;
  bodyStyles?: Record<string, unknown>;
  alternateRowStyles?: Record<string, unknown>;
  margin?: { top?: number; left?: number; right?: number; bottom?: number };
  didDrawPage?: (data: { pageNumber: number }) => void;
  columnStyles?: Record<number, { cellWidth?: number | 'auto' | 'wrap' }>;
}

const DEFAULT_PDF_OPTIONS: PdfOptions = {
  ...DEFAULT_FORMAT_OPTIONS,
  title: 'Data Export',
  includeTimestamp: true,
  pageSize: 'letter',
  orientation: 'portrait',
};

const COLORS = {
  primary: [59, 130, 246] as [number, number, number], // Blue
  headerBg: [243, 244, 246] as [number, number, number], // Gray-100
  textDark: [31, 41, 55] as [number, number, number], // Gray-800
  textMuted: [107, 114, 128] as [number, number, number], // Gray-500
};

/**
 * Dynamically load jsPDF and its autotable plugin
 * This saves ~411KB from the initial bundle
 */
async function loadJsPDF(): Promise<{ jsPDF: new (options: { orientation: string; unit: string; format: string }) => JsPDFInstance }> {
  const [{ jsPDF }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'), // Side-effect import to extend jsPDF
  ]);
  return { jsPDF: jsPDF as unknown as new (options: { orientation: string; unit: string; format: string }) => JsPDFInstance };
}

/**
 * Generate PDF export from data
 */
export async function generatePDF(
  data: Record<string, unknown>[],
  columns: ColumnConfig[],
  entityName: string,
  options: Partial<PdfOptions> = {}
): Promise<Blob> {
  const opts = { ...DEFAULT_PDF_OPTIONS, ...options };

  const { jsPDF } = await loadJsPDF();
  const doc = new jsPDF({
    orientation: opts.orientation,
    unit: 'mm',
    format: opts.pageSize,
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  // Title
  doc.setFontSize(20);
  doc.setTextColor(...COLORS.primary);
  doc.text(opts.title, margin, 20);

  // Subtitle with entity name and count
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.textMuted);
  const subtitle = `${formatEntityName(entityName)} - ${data.length} records`;
  doc.text(subtitle, margin, 28);

  // Timestamp
  if (opts.includeTimestamp) {
    const timestamp = format(new Date(), 'MMMM d, yyyy h:mm a');
    doc.setFontSize(10);
    doc.text(`Exported: ${timestamp}`, margin, 35);
  }

  // Prepare table data
  const headers = columns.map((col) => col.header);
  const body = data.map((record) =>
    columns.map((col) => {
      const rawValue = record[col.key];
      return formatValue(rawValue, col.type, opts as FormatOptions);
    })
  );

  // Add table
  doc.autoTable({
    head: [headers],
    body,
    startY: opts.includeTimestamp ? 42 : 35,
    theme: 'striped',
    headStyles: {
      fillColor: COLORS.headerBg,
      textColor: COLORS.textDark,
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: {
      fontSize: 8,
      textColor: COLORS.textDark,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    margin: { top: margin, left: margin, right: margin, bottom: margin },
    didDrawPage: (data) => {
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.textMuted);
      const footerText = `Page ${data.pageNumber}`;
      doc.text(footerText, pageWidth / 2, pageHeight - 10, { align: 'center' });
      doc.text('TrainerPulse', margin, pageHeight - 10);
    },
  });

  return doc.output('blob');
}

/**
 * Generate PDF export for multiple entities
 */
export async function generateMultiEntityPDF(
  entityData: Record<string, { data: Record<string, unknown>[]; columns: ColumnConfig[] }>,
  options: Partial<PdfOptions> = {}
): Promise<Blob> {
  const opts = { ...DEFAULT_PDF_OPTIONS, ...options };

  const { jsPDF } = await loadJsPDF();
  const doc = new jsPDF({
    orientation: opts.orientation,
    unit: 'mm',
    format: opts.pageSize,
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  // Title page
  doc.setFontSize(24);
  doc.setTextColor(...COLORS.primary);
  doc.text(opts.title, pageWidth / 2, 40, { align: 'center' });

  doc.setFontSize(14);
  doc.setTextColor(...COLORS.textMuted);
  doc.text('Data Export Report', pageWidth / 2, 52, { align: 'center' });

  if (opts.includeTimestamp) {
    const timestamp = format(new Date(), 'MMMM d, yyyy h:mm a');
    doc.setFontSize(12);
    doc.text(timestamp, pageWidth / 2, 65, { align: 'center' });
  }

  // Summary section
  let yPos = 90;
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.textDark);
  doc.text('Export Summary', margin, yPos);

  yPos += 10;
  doc.setFontSize(11);
  for (const [entityName, { data }] of Object.entries(entityData)) {
    doc.text(`• ${formatEntityName(entityName)}: ${data.length} records`, margin + 5, yPos);
    yPos += 7;
  }

  // Add each entity on new pages
  for (const [entityName, { data, columns }] of Object.entries(entityData)) {
    doc.addPage();

    // Entity title
    doc.setFontSize(16);
    doc.setTextColor(...COLORS.primary);
    doc.text(formatEntityName(entityName), margin, 20);

    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textMuted);
    doc.text(`${data.length} records`, margin, 27);

    // Prepare table data
    const headers = columns.map((col) => col.header);
    const body = data.map((record) =>
      columns.map((col) => {
        const rawValue = record[col.key];
        return formatValue(rawValue, col.type, opts as FormatOptions);
      })
    );

    // Add table
    doc.autoTable({
      head: [headers],
      body,
      startY: 32,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.headerBg,
        textColor: COLORS.textDark,
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: {
        fontSize: 8,
        textColor: COLORS.textDark,
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251],
      },
      margin: { top: margin, left: margin, right: margin, bottom: margin },
      didDrawPage: (pageData) => {
        // Footer
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.textMuted);
        const footerText = `Page ${pageData.pageNumber}`;
        doc.text(footerText, pageWidth / 2, pageHeight - 10, { align: 'center' });
        doc.text('TrainerPulse', margin, pageHeight - 10);
      },
    });
  }

  return doc.output('blob');
}

/**
 * Generate a client summary PDF
 */
export async function generateClientSummaryPDF(
  clientData: {
    name: string;
    email: string;
    phone?: string;
    status: string;
    sessions?: Record<string, unknown>[];
    measurements?: Record<string, unknown>[];
    workouts?: Record<string, unknown>[];
  },
  options: Partial<PdfOptions> = {}
): Promise<Blob> {
  const opts = { ...DEFAULT_PDF_OPTIONS, title: `Client Report: ${clientData.name}`, ...options };

  const { jsPDF } = await loadJsPDF();
  const doc = new jsPDF({
    orientation: opts.orientation,
    unit: 'mm',
    format: opts.pageSize,
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;

  // Header
  doc.setFontSize(20);
  doc.setTextColor(...COLORS.primary);
  doc.text('Client Report', margin, 20);

  // Client info
  doc.setFontSize(16);
  doc.setTextColor(...COLORS.textDark);
  doc.text(clientData.name, margin, 35);

  doc.setFontSize(11);
  doc.setTextColor(...COLORS.textMuted);
  let yPos = 45;
  doc.text(`Email: ${clientData.email}`, margin, yPos);
  yPos += 7;
  if (clientData.phone) {
    doc.text(`Phone: ${clientData.phone}`, margin, yPos);
    yPos += 7;
  }
  doc.text(`Status: ${clientData.status}`, margin, yPos);
  yPos += 7;

  if (opts.includeTimestamp) {
    const timestamp = format(new Date(), 'MMMM d, yyyy');
    doc.text(`Report Date: ${timestamp}`, margin, yPos);
  }

  // Summary stats
  yPos += 15;
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.textDark);
  doc.text('Summary', margin, yPos);

  yPos += 10;
  doc.setFontSize(11);
  doc.text(`Total Sessions: ${clientData.sessions?.length || 0}`, margin + 5, yPos);
  yPos += 7;
  doc.text(`Measurements Recorded: ${clientData.measurements?.length || 0}`, margin + 5, yPos);
  yPos += 7;
  doc.text(`Workouts Completed: ${clientData.workouts?.length || 0}`, margin + 5, yPos);

  return doc.output('blob');
}

/**
 * Format entity name for display
 */
function formatEntityName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Create a downloadable PDF file
 */
export function createPDFDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
